import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const WidgetV2 = ({
  children,
  title,
  actions,
  sxCard,
  sxContent,
  collapse,
  insideTab,
  initialExpanded,
  subheader,
  avatar,
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);

  const visibleActions = actions.filter((a) => a.show === undefined || !!a.show);
  const hasTitle = Boolean(title?.trim());
  const hasSubheader = Boolean(subheader);
  const hasHeaderActions = visibleActions.length > 0 || collapse;
  const showHeader = hasTitle || hasSubheader || hasHeaderActions;
  const titleOnlyHeader = hasTitle || hasSubheader;

  const getHeaderActions = () => (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={insideTab ? 0.5 : 1}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ rowGap: insideTab ? 0.5 : 1 }}
    >
      {visibleActions
        .filter((a) => a.isComponent)
        .map((a) => a.component)}
      {visibleActions
        .filter((a) => !a.isComponent)
        .map((a) => (
          <Button
            key={a.title}
            color="primary"
            variant="contained"
            size={insideTab ? 'small' : 'medium'}
            onClick={a.onClick}
            sx={(theme) => ({
              fontWeight: 700,
              minWidth: insideTab ? 'auto' : 112,
              px: insideTab ? 1.25 : 2.5,
              whiteSpace: 'nowrap',
              boxShadow: insideTab
                ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`
                : `0 4px 16px ${alpha(theme.palette.primary.main, 0.42)}`,
              '&:hover:not(:disabled)': {
                boxShadow: insideTab
                  ? `0 3px 10px ${alpha(theme.palette.primary.main, 0.42)}`
                  : `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                transform: insideTab ? 'none' : 'translateY(-1px)',
              },
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.short,
              }),
            })}
          >
            {a.title}
          </Button>
        ))}
      {collapse && (
        <IconButton
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label="show more"
          sx={{
            color: 'common.white',
            ml: 'auto',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: (theme) =>
              theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
              }),
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      )}
    </Stack>
  );

  return (
    <Card
      sx={{
        boxShadow: 1,
        borderRadius: 1,
        border: (theme) => `0.5px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        ...sxCard,
      }}
    >
      {showHeader && (
        <CardHeader
          avatar={avatar || undefined}
          title={hasTitle ? title : undefined}
          subheader={subheader}
          action={getHeaderActions()}
          sx={{
            position: 'relative',
            boxSizing: 'border-box',
            py: titleOnlyHeader ? { xs: 1.75, sm: 2 } : 1,
            px: { xs: 2, sm: 2.5 },
            bgcolor: (theme) => theme.palette.neutral[900],
            borderBottom: (theme) =>
              `1px solid ${alpha(
                theme.palette.mode === 'dark'
                  ? theme.palette.neutral[50]
                  : theme.palette.neutral[900],
                0.08
              )}`,
            ...(!titleOnlyHeader && {
              '& .MuiCardHeader-content': {
                display: 'none',
                width: 0,
                p: 0,
                m: 0,
              },
              '& .MuiCardHeader-action': {
                m: 0,
                ml: 'auto',
                alignSelf: 'center',
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
              },
            }),
            '&::after': {
              background: (theme) =>
                `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.35)} 55%, transparent 100%)`,
              bottom: 0,
              content: '""',
              height: 3,
              left: 0,
              opacity: 0.9,
              position: 'absolute',
              width: '38%',
            },
            '& .MuiCardHeader-avatar': {
              mr: 1.5,
              color: 'common.white',
            },
            '& .MuiCardHeader-title': {
              color: 'common.white',
              fontWeight: 600,
              letterSpacing: 0.3,
              ...(insideTab && { fontSize: 14 }),
            },
            '& .MuiCardHeader-subheader': {
              color: (theme) => theme.palette.neutral[400],
            },
          }}
        />
      )}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, ...sxContent }}>
          {children}
        </CardContent>
      </Collapse>
    </Card>
  );
};

WidgetV2.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  actions: PropTypes.array,
  sxCard: PropTypes.object,
  sxContent: PropTypes.object,
  collapse: PropTypes.bool,
  insideTab: PropTypes.bool,
  initialExpanded: PropTypes.bool,
  subheader: PropTypes.node,
  avatar: PropTypes.node,
};

WidgetV2.defaultProps = {
  title: undefined,
  actions: [],
  sxCard: {},
  sxContent: {},
  collapse: false,
  insideTab: false,
  initialExpanded: true,
  subheader: undefined,
  avatar: undefined,
};

export default WidgetV2;
